import React, { FC, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Button, Icon, RobotoFontFamily, TextBadge, Typography } from '@packages/ui/shared';
import { PublicationPreviewContent } from '@web/vin-sessions/types';
import { StyleProp } from 'react-native/Libraries/StyleSheet/StyleSheet';
import { TextStyle } from 'react-native/Libraries/StyleSheet/StyleSheetTypes';
import colors from '@packages/core/styles/colors';
import { createUseStyles } from 'react-jss';
import { analyticsService, docsService } from '@web/services/singletons';
import { sanitizeInline } from '@web/utils';
import { openDocumentTab, resolveDocumentUrl } from '@web/vin-sessions/utils/open-document-tab';
import { useAuthState } from '@packages/contexts/auth';
import { useGoogleAnalytics } from '@web/core/google-analytics/use-google-analytics';
import { GoogleAnalyticsEvents } from '@web/core/google-analytics';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { useParams } from 'react-router-dom';

export const KeywordPreview: FC<{ headline: string; style: React.CSSProperties }> = ({ headline, style }) => {
    const cleanHeadline = sanitizeInline(headline?.replace(/\r?\n|\r/g, '').trim());
    return <p dangerouslySetInnerHTML={{ __html: cleanHeadline }} style={style} />;
};

export interface DocumentPreviewModalProps {
    show: boolean;
    title: string;
    footer?: boolean;
    cancelButton?: string;
    confirmButton?: string;
    content?: PublicationPreviewContent;
    onHideModal: () => void;
    closeButton?: boolean;
    handleScroll: (
        event: React.UIEvent<HTMLDivElement>,
        id: string,
        title: string,
        totalPages: number,
        docType?: string
    ) => void;
    setPdfPreview: React.Dispatch<React.SetStateAction<{ url: string; keyword: string; pageNumber: number } | null>>;
    highlightedWords: string[] | undefined;
}

const useStyles = createUseStyles({
    scrollBar: {
        overflowY: 'scroll',
        '&::-webkit-scrollbar': {
            width: 10,
            display: 'block !important',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: '#ffffff',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.blueFour,
            borderRadius: 10,
            border: '2px solid #ffffff',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            cursor: 'pointer',
            backgroundColor: colors.blueOne,
        },
    },
});

export const DocumentPreviewModal: FC<DocumentPreviewModalProps> = ({
    show,
    title,
    footer = true,
    cancelButton,
    confirmButton,
    content,
    closeButton,
    onHideModal,
    handleScroll,
    setPdfPreview,
    highlightedWords,
}) => {
    const styles = useStyles();
    const { account, token } = useAuthState();
    const { googleAnalyticsService } = useGoogleAnalytics();
    const { vin } = useParams<{ vin: string }>();

    const typographyStyles = {
        modalHeaderStyle: {
            fontFamily: 'Roboto',
            fontWeight: 600,
            fontSize: 15,
            lineHeight: 16,
            letterSpacing: 0,
        } as unknown as StyleProp<TextStyle>,
        paragraphStyle: {
            width: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'inline-block',
            marginBottom: 0,
            fontSize: 15,
            fontWeight: 400,
            fontFamily: RobotoFontFamily.Regular,
        } as React.CSSProperties,
        badgeStyle: {
            fontFamily: 'Roboto',
            fontWeight: '700',
            fontSize: 11,
            letterSpacing: 0,
            textAlign: 'center',
            color: '#1F518F',
            textTransform: 'none',
            paddingLeft: 4,
            paddingRight: 4,
        } as unknown as StyleProp<TextStyle>,
    };

    return (
        <Modal show={show} centered={true} onHide={onHideModal} size="lg">
            {title && (
                <Modal.Header closeButton={closeButton}>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
            )}

            <Modal.Body
                style={{ maxHeight: '472px', overflowY: 'scroll', overflowX: 'hidden' }}
                className={styles.scrollBar}
                onScroll={(event: React.UIEvent<HTMLDivElement>) =>
                    content && handleScroll
                        ? handleScroll(event, content.documentId, content.title, content.totalPages, content?.docType)
                        : undefined
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {content && (
                        <Typography style={typographyStyles.modalHeaderStyle}>
                            {content.totalElements} {content.totalElements > 1 ? 'Results' : 'Result'} for "
                            {content.query}" in {content.title}
                        </Typography>
                    )}
                </div>
                {content?.content.map((card) => {
                    const handlePreviewCardClick = async () => {
                        setPdfPreview({
                            url: card.url,
                            keyword: content.query,
                            pageNumber: card.pageNumber,
                        });
                        googleAnalyticsService.track(GoogleAnalyticsEvents.DocumentPreview, {
                            document_id: card.documentId,
                            document_type: content?.docType,
                            entry_point: 'keywords_preview',
                        });
                        analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_PREVIEWED, {
                            screen: 'stis_publications',
                            source: 'stis_preview',
                            documentId: card.documentId,
                            type: content?.docType,
                            docType: content?.docType,
                            createdDate: content?.createdDate,
                            lastUpdated: content?.lastUpdated,
                            searchQuery: content?.query,
                            vin: vin,
                            vinPresent: !!vin,
                        });

                        account && (await docsService.saveDocumentToRecents({ docId: card.documentId }));
                        account &&
                            (await docsService.dummyPostOperation({
                                body: {
                                    documentId: card.documentId,
                                    pageNumber: card.pageNumber,
                                    documentPageId: card.documentPageId,
                                },
                                ignoreCache: true,
                            }));
                    };

                    return (
                        <div
                            key={card.documentId}
                            className="d-flex justify-content-between align-items-center p-2 rounded mt-4 mb-2"
                            style={{ border: `1px solid ${'#999DA3'}`, cursor: account ? 'pointer' : 'default' }}
                            {...(account && {
                                onClick: handlePreviewCardClick,
                            })}
                        >
                            <div
                                style={{
                                    maxWidth: '85%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <KeywordPreview headline={card.headline} style={typographyStyles.paragraphStyle} />
                            </div>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <TextBadge
                                    variant="blue"
                                    textStyle={typographyStyles.badgeStyle}
                                    {...(account && {
                                        onClick: (event?: any) => {
                                            event?.stopPropagation?.();
                                            void handlePreviewCardClick();
                                        },
                                    })}
                                >
                                    Page {card.pageNumber}
                                </TextBadge>
                                {account && (
                                    <Icon
                                        name="open-in-new"
                                        color="grayFive"
                                        onPress={async (event?: any) => {
                                            event?.stopPropagation?.();

                                            const url = card.url;
                                            const highlightedWordsParam = highlightedWords?.join(',') || '';
                                            const previewWindow = window.open(
                                                `/preview?highlightedWords=${encodeURIComponent(
                                                    highlightedWordsParam
                                                )}&pageNumber=${card.pageNumber}&url=${encodeURIComponent(url)}`,
                                                '_blank'
                                            );

                                            if (previewWindow) {
                                                analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_PREVIEWED, {
                                                    screen: 'stis_publications',
                                                    source: 'stis_preview',
                                                    documentId: card.documentId,
                                                    type: content?.docType,
                                                    docType: content?.docType,
                                                    createdDate: content?.createdDate,
                                                    lastUpdated: content?.lastUpdated,
                                                    searchQuery: content?.query,
                                                    vin: vin,
                                                    vinPresent: !!vin,
                                                });

                                                googleAnalyticsService.track(GoogleAnalyticsEvents.DocumentOpen, {
                                                    success: true,
                                                    entry_point: 'preview_modal',
                                                    document_id: card.documentId,
                                                    document_type: content?.docType,
                                                });
                                            } else {
                                                analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_OPEN_FAILED, {
                                                    screen: 'stis_publications',
                                                    source: 'stis_preview',
                                                    documentId: card.documentId,
                                                    type: content?.docType,
                                                    errorMessage: 'Preview popup blocked by browser',
                                                    vin: vin,
                                                    vinPresent: !!vin,
                                                });

                                                googleAnalyticsService.track(GoogleAnalyticsEvents.DocumentOpen, {
                                                    success: false,
                                                    entry_point: 'preview_modal',
                                                    document_id: card.documentId,
                                                    document_type: content?.docType,
                                                    error_type: 'popup_blocked',
                                                });
                                            }

                                            account &&
                                                (await docsService.saveDocumentToRecents({ docId: card.documentId }));
                                            account &&
                                                (await docsService.dummyPostOperation({
                                                    body: {
                                                        documentId: card.documentId,
                                                        pageNumber: card.pageNumber,
                                                        documentPageId: card.documentPageId,
                                                    },
                                                    ignoreCache: true,
                                                }));
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </Modal.Body>

            {footer && (
                <Modal.Footer>
                    {cancelButton && (
                        <Button variant="ghost-blue" onPress={onHideModal} style={{ marginRight: 5 }}>
                            {cancelButton}
                        </Button>
                    )}
                    {confirmButton && (
                        <Button
                            variant={'primary'}
                            onPress={async () => {
                                // Always open the full document through the CDN resolver (bookmarkable),
                                // rather than the S3 pre-signed URL carried on the preview content.
                                const documentOpened = await openDocumentTab(() =>
                                    resolveDocumentUrl(
                                        { documentId: content?.documentId ?? '', docType: content?.docType },
                                        account,
                                        token
                                    )
                                );

                                if (documentOpened) {
                                    googleAnalyticsService.track(GoogleAnalyticsEvents.DocumentOpen, {
                                        success: true,
                                        entry_point: 'preview_modal',
                                        document_id: content?.documentId,
                                        document_type: content?.docType,
                                    });
                                    analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_OPENED, {
                                        screen: 'stis_publications',
                                        source: 'stis_preview_modal',
                                        documentId: content?.documentId,
                                        type: content?.docType,
                                        docType: content?.docType,
                                        createdDate: content?.createdDate,
                                        lastUpdated: content?.lastUpdated,
                                        vin: vin,
                                        vinPresent: !!vin,
                                    });
                                } else {
                                    analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_OPEN_FAILED, {
                                        screen: 'stis_publications',
                                        source: 'stis_preview_modal',
                                        documentId: content?.documentId,
                                        type: content?.docType,
                                        errorMessage: 'Document tab was blocked or URL resolution failed',
                                        vin: vin,
                                        vinPresent: !!vin,
                                    });
                                    googleAnalyticsService.track(GoogleAnalyticsEvents.DocumentOpen, {
                                        success: false,
                                        entry_point: 'preview_modal',
                                        document_id: content?.documentId,
                                        document_type: content?.docType,
                                    });
                                }

                                account &&
                                    (await docsService.saveDocumentToRecents({
                                        docId: content ? content.documentId : '',
                                    }));
                            }}
                        >
                            {confirmButton}
                        </Button>
                    )}
                </Modal.Footer>
            )}
        </Modal>
    );
};
